import type { Event } from '../types/api';
import { parseEventLocalDateTime, hasKnownEventTime } from './eventDateTime';
import { getEventAddress, getEventFormat, getVenueLabel } from './eventPresentation';
import { resolveEventTimeZone } from './eventTimeZone';
import { buildEventUrl, toSlug } from './slug';

/**
 * ICS (RFC 5545) generation for "Add to Calendar".
 *
 * Dates are stored as source-local wall clock with no offset. We therefore emit
 * a floating or TZID-qualified local time rather than converting to UTC:
 *
 *   - resolvable venue zone  -> DTSTART;TZID=America/Los_Angeles:20260821T090000
 *   - no resolvable zone     -> DTSTART:20260821T090000 (floating local time)
 *   - no reliable time       -> DTSTART;VALUE=DATE:20260821 (all-day)
 *
 * A floating time is read by every calendar client as "this wall clock, in
 * whatever zone you are in", which is the honest representation when the
 * source never told us the zone.
 */

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function formatIcsDate(value: string): string | null {
  const parsed = parseEventLocalDateTime(value);
  if (!parsed) return null;
  return `${parsed.year}${pad(parsed.month)}${pad(parsed.day)}`;
}

function formatIcsDateTime(value: string): string | null {
  const parsed = parseEventLocalDateTime(value);
  if (!parsed) return null;
  return `${parsed.year}${pad(parsed.month)}${pad(parsed.day)}T${pad(parsed.hour)}${pad(parsed.minute)}${pad(parsed.second)}`;
}

/** Escape per RFC 5545 §3.3.11. */
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

/** Fold lines at 75 octets as required by RFC 5545 §3.1. */
function foldLine(line: string): string {
  if (line.length <= 75) return line;
  const chunks: string[] = [line.slice(0, 75)];
  let rest = line.slice(75);
  while (rest.length > 74) {
    chunks.push(` ${rest.slice(0, 74)}`);
    rest = rest.slice(74);
  }
  if (rest) chunks.push(` ${rest}`);
  return chunks.join('\r\n');
}

function nextDay(value: string): string | null {
  const parsed = parseEventLocalDateTime(value);
  if (!parsed) return null;
  const date = new Date(parsed.year, parsed.month - 1, parsed.day + 1);
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
}

/** Build the ICS document for a single event. Returns '' if it has no start. */
export function buildEventIcs(event: Event, origin?: string): string {
  if (!event?.startDate) return '';

  const format = getEventFormat(event);
  const zone = resolveEventTimeZone(event, format, event.startDate);
  const tzParam = zone.timeZone ? `;TZID=${zone.timeZone}` : '';
  const canonicalUrl = buildEventUrl(event, origin);

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Event Finder//Event Export//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:event-${event.eventId}@eventfinder`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}`,
  ];

  if (hasKnownEventTime(event.startDate)) {
    lines.push(`DTSTART${tzParam}:${formatIcsDateTime(event.startDate)}`);
    const end = hasKnownEventTime(event.endDate) ? formatIcsDateTime(event.endDate) : null;
    if (end) lines.push(`DTEND${tzParam}:${end}`);
  } else {
    const start = formatIcsDate(event.startDate);
    if (!start) return '';
    lines.push(`DTSTART;VALUE=DATE:${start}`);
    // All-day DTEND is exclusive, so add one day past the last day.
    const endSource = event.endDate || event.startDate;
    const end = nextDay(endSource);
    if (end) lines.push(`DTEND;VALUE=DATE:${end}`);
  }

  lines.push(`SUMMARY:${escapeIcsText(event.title ?? 'Event')}`);

  const locationParts = format === 'virtual'
    ? ['Virtual event']
    : [getVenueLabel(event), getEventAddress(event)].filter(Boolean);
  if (format === 'hybrid') locationParts.unshift('Hybrid event');
  if (locationParts.length > 0) {
    lines.push(`LOCATION:${escapeIcsText(Array.from(new Set(locationParts)).join(' — '))}`);
  }

  const descriptionParts: string[] = [];
  if (event.description) descriptionParts.push(event.description.trim());
  if (event.registrationUrl) descriptionParts.push(`Register: ${event.registrationUrl}`);
  if (canonicalUrl) descriptionParts.push(`Event page: ${canonicalUrl}`);
  if (descriptionParts.length > 0) {
    lines.push(`DESCRIPTION:${escapeIcsText(descriptionParts.join('\n\n'))}`);
  }

  if (canonicalUrl) lines.push(`URL:${escapeIcsText(canonicalUrl)}`);

  lines.push('END:VEVENT', 'END:VCALENDAR');

  return `${lines.map(foldLine).join('\r\n')}\r\n`;
}

/** Download the event as an .ics file. */
export function downloadEventIcs(event: Event): void {
  const ics = buildEventIcs(event);
  if (!ics) return;

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${toSlug(event.title) || `event-${event.eventId}`}.ics`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export type ShareOutcome = 'shared' | 'copied' | 'failed';

/**
 * Share via the Web Share API when available, otherwise copy the canonical URL.
 * Returns what actually happened so the caller can show the right toast.
 */
export async function shareEvent(event: Event): Promise<ShareOutcome> {
  const url = buildEventUrl(event);
  if (!url) return 'failed';

  const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
  if (typeof nav.share === 'function') {
    try {
      await nav.share({ title: event.title ?? 'Event', text: event.title ?? undefined, url });
      return 'shared';
    } catch (error) {
      // A user-cancelled share must not fall through to a surprise clipboard write.
      if (error instanceof DOMException && error.name === 'AbortError') return 'failed';
    }
  }

  return (await copyToClipboard(url)) ? 'copied' : 'failed';
}

/** Clipboard write with a document.execCommand fallback for older browsers. */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to the legacy path */
    }
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
